import { AxiosResponse } from "axios";
import laravelApi from "./api";
import { Roles } from "@/models/user/roles";
import {
  AuthError,
  DuplicateCustomerError,
  NotFoundError
} from "./error_service";
import {
  CreateNetSuiteCustomerPayload,
  CreateNetSuiteQuotePayload,
  DuplicateCustomerMatch,
  FoundNetSuiteItemBySku,
  NetSuiteCustomer,
  NetSuiteEmployee,
  NetSuiteQuote,
  NetSuiteQuoteLineItem,
  PartialNetSuiteCustomer,
  PaymentTerm,
  PriceLevel
} from "@/models/netsuite/netsuite";

/**
 * The axios instance is used directly rather than through a module-scope alias
 * (`const netSuiteApi = laravelApi`). This module sits on an import cycle —
 * api.ts imports the store, the store's order actions import
 * customer_details_prefetch, and that imports this file — so when this module
 * is evaluated, api.ts may still be mid-evaluation and its default export not
 * yet assigned. An alias would capture that `undefined` permanently and every
 * request here would throw "Cannot read properties of undefined (reading
 * 'get')". Referencing `laravelApi` at call time resolves the live binding
 * after the cycle settles, which is what the other services already do.
 */

/**
 * A `responseType: "blob"` request wraps an error JSON body in a Blob too, so
 * the usual `err.response.data.error` read comes back undefined. Read the blob
 * back as text and pull the API's message out of it.
 */
function readBlobErrorMessage(err: any, fallback: string): Promise<string> {
  const data = err?.response?.data;
  if (!(data instanceof Blob)) {
    return Promise.resolve(
      data?.error ?? data?.message ?? err?.message ?? fallback
    );
  }
  return new Promise<string>(resolve => {
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        resolve(parsed?.error ?? parsed?.message ?? fallback);
      } catch (e) {
        resolve(err?.message ?? fallback);
      }
    };
    reader.onerror = () => resolve(err?.message ?? fallback);
    reader.readAsText(data);
  });
}

export default class NetSuiteService {
  public async getNetSuiteEmployeeId(userEmail: string): Promise<number> {
    try {
      let employee = {} as NetSuiteEmployee;
      const queryEmail = process.env.VUE_APP_TEST_REP_EMAIL
        ? process.env.VUE_APP_TEST_REP_EMAIL
        : userEmail;
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_employee_by_email/${queryEmail}`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      employee = res.data.rows[0];
      if (employee && employee.id) {
        return employee.id;
      } else
        throw new AuthError(
          "Unable to properly authenticate and connect. Please contact the administrator.",
          500
        );
    } catch (err) {
      throw new AuthError(err.response.data.message, err.response.status);
    }
  }

  public async getRepNetSuiteQuotes(
    repNetSuiteId: number
  ): Promise<NetSuiteQuote[]> {
    let quotes = [] as NetSuiteQuote[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_quotes_by_rep/${repNetSuiteId}/open`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      quotes = res.data.rows || [];
    } catch (err) {
      console.error("Error getting the rep's open NetSuite quotes", err);
    }
    return quotes;
  }

  public async getAllNetSuiteQuotes(): Promise<NetSuiteQuote[]> {
    let quotes = [] as NetSuiteQuote[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_quotes/open`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      quotes = res.data.rows || [];
    } catch (err) {
      console.error("Error getting the open NetSuite quotes", err);
    }
    return quotes;
  }
  public async getNetSuiteQuoteLineItemsById(
    quoteNetSuiteId: number
  ): Promise<NetSuiteQuoteLineItem[]> {
    let netSuiteQuoteLineItems = [] as NetSuiteQuoteLineItem[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_quote_lineitems/${quoteNetSuiteId}`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      netSuiteQuoteLineItems = res.data.rows;
      // console.log(
      //   "# of Open NetSuite Quote Line Items:",
      //   netSuiteQuoteLineItems.length
      // );
      // console.log("netSuiteQuoteLineItems", netSuiteQuoteLineItems);
    } catch (err) {
      console.error("Error getting NetSuite quote line items", err);
    }
    return netSuiteQuoteLineItems;
  }

  public async getCustomerNetSuiteQuotes(
    customerNetSuiteId: number
  ): Promise<NetSuiteQuote[]> {
    let quotes = [] as NetSuiteQuote[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_customer_quotes/${customerNetSuiteId}/open`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      quotes = res.data.rows;
    } catch (err) {
      console.error("Error getting the customer's open NetSuite quotes", err);
    }
    return quotes;
  }

  public async getPartialNetSuiteCustomers(
    repNetSuiteId?: number
  ): Promise<PartialNetSuiteCustomer[]> {
    let netSuiteCustomers = [] as PartialNetSuiteCustomer[];
    try {
      const netSuiteApiUrl = repNetSuiteId
        ? `/api/netsuite/suiteql/get_customers/${repNetSuiteId}`
        : `/api/netsuite/suiteql/get_customers`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      netSuiteCustomers = res.data;
    } catch (err) {
      console.error("Error getting the NetSuite customers", err);
    }
    return netSuiteCustomers;
  }

  public async getMyCustomers(): Promise<PartialNetSuiteCustomer[]> {
    let netSuiteCustomers = [] as PartialNetSuiteCustomer[];
    try {
      const res: AxiosResponse = await laravelApi.get("/api/netsuite/suiteql/get_my_customers");
      netSuiteCustomers = res.data;
    } catch (err) {
      console.error("Error getting my NetSuite customers", err);
    }
    return netSuiteCustomers;
  }

  /**
   * The customers this user is allowed to quote for: every customer for an
   * admin, the rep's own book otherwise. One place decides that split, so a
   * caller refreshing the list can't disagree with the one that loaded it.
   */
  public getCustomersForRole(
    userRole: string | null
  ): Promise<PartialNetSuiteCustomer[]> {
    return userRole === Roles.Admin
      ? this.getPartialNetSuiteCustomers()
      : this.getMyCustomers();
  }

  public async getMyQuotes(): Promise<NetSuiteQuote[]> {
    let quotes = [] as NetSuiteQuote[];
    try {
      const res: AxiosResponse = await laravelApi.get("/api/netsuite/suiteql/get_my_quotes/open");
      quotes = res.data.rows || [];
    } catch (err) {
      console.error("Error getting my NetSuite quotes", err);
    }
    return quotes;
  }

  public async getNetSuiteCustomerById(
    customerNetSuiteId: string
  ): Promise<NetSuiteCustomer> {
    let netSuiteCustomer = {} as NetSuiteCustomer;
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_customer/${customerNetSuiteId}`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      netSuiteCustomer = res.data;
    } catch (err) {
      console.error("Error getting the NetSuite customer", err);
    }
    return netSuiteCustomer;
  }

  // Note: FNSI-101 - call this function when creating a new quote, determining if a temporary NetSuite item needs to be used for a custom SKU or if the found SKU can be used.
  public async getNetSuiteItemBySku(
    itemSku: string
  ): Promise<FoundNetSuiteItemBySku> {
    let foundNetSuiteItem = {} as FoundNetSuiteItemBySku;
    try {
      const netSuiteApiUrl = `/api/netsuite/inventory/get_item_by_sku/${encodeURIComponent(itemSku)}`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      if (!res.data || !res.data.rows || res.data.rows.length === 0) {
        throw new NotFoundError(
          `No NetSuite item found with the provided SKU: ${itemSku}`,
          404
        );
      } else if (res.data.rows.length > 1) {
        throw new NotFoundError(
          "Multiple items found with the provided SKU.",
          404
        );
      }
      foundNetSuiteItem = res.data.rows[0];
    } catch (err) {
      console.error(err.message);
    }
    return foundNetSuiteItem;
  }

  /**
   * FUPOF-191: NetSuite entities that could already be the company described by
   * `companyName` / `email` — the check behind the new-customer duplicate
   * warning. Never throws: a lookup that fails must not stand between the rep
   * and their quote, it just means no warning is shown (create_customer's own
   * check is the backstop).
   */
  public async checkDuplicateCustomers(
    companyName: string,
    email: string
  ): Promise<DuplicateCustomerMatch[]> {
    try {
      const res: AxiosResponse = await laravelApi.post(
        `/api/netsuite/customers/check_duplicates`,
        { company_name: companyName, email }
      );
      return res.data?.matches ?? [];
    } catch (err) {
      console.error("[NetSuiteService] checkDuplicateCustomers error:", err);
      return [];
    }
  }

  /**
   * Create the prospect. The API refuses (409) when NetSuite already has the
   * company, which surfaces here as a DuplicateCustomerError carrying the
   * matches — the caller shows them and either switches the quote to an
   * existing customer or re-calls with `allowDuplicate` to mean it.
   */
  public async createNetSuiteCustomer(
    payload: CreateNetSuiteCustomerPayload,
    allowDuplicate = false
  ): Promise<any> {
    const netSuiteApiUrl = `/api/netsuite/suiteql/create_customer`;
    try {
      const res: AxiosResponse = await laravelApi.post(
        netSuiteApiUrl,
        allowDuplicate ? { ...payload, allow_duplicate: true } : payload
      );
      return res.data;
    } catch (err) {
      const e = err as any;
      console.error(
        "[NetSuiteService] createNetSuiteCustomer error:",
        e?.response?.data ?? e
      );
      if (e?.response?.status === 409) {
        throw new DuplicateCustomerError(
          e.response.data?.message ??
            "This customer already exists in NetSuite.",
          e.response.data?.matches ?? []
        );
      }
      throw new Error(
        e?.response?.data?.message ??
          e?.message ??
          "Unknown error creating NetSuite customer."
      );
    }
  }

  // ToDo: FNSI-101 - new POST endpoint to Laravel API to create new NetSuite quote
  public async createNetSuiteQuote(
    payload: CreateNetSuiteQuotePayload
  ): Promise<any> {
    const netSuiteApiUrl = `/api/netsuite/suiteql/create_quote`;
    try {
      const res: AxiosResponse = await laravelApi.post(
        netSuiteApiUrl,
        payload
      );
      return res.data;
    } catch (err) {
      const e = err as any;
      console.error(
        "[NetSuiteService] createNetSuiteQuote error:",
        e?.response?.data ?? e
      );
      throw new Error(
        e?.response?.data?.message ??
          e?.message ??
          "Unknown error creating NetSuite quote."
      );
    }
  }

  public async sendQuoteEmail(quoteId: number): Promise<void> {
    const netSuiteApiUrl = `/api/netsuite/suiteql/send_quote_email/${quoteId}`;
    await laravelApi.post(netSuiteApiUrl);
  }

  public async getQuotePdf(quoteId: number): Promise<Blob> {
    const netSuiteApiUrl = `/api/netsuite/suiteql/get_quote_pdf/${quoteId}`;
    const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl, {
      responseType: "blob"
    });
    return res.data;
  }

  /**
   * FUPOF-191: the Sales Order PDF for a converted quote. `salesOrderId` is the
   * quote row's "Sales Order ID", which is null until the quote converts — only
   * call this for rows that carry one.
   */
  public async getSalesOrderPdf(salesOrderId: number): Promise<Blob> {
    const netSuiteApiUrl = `/api/netsuite/suiteql/get_salesorder_pdf/${salesOrderId}`;
    try {
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl, {
        responseType: "blob"
      });
      return res.data;
    } catch (err) {
      const message = await readBlobErrorMessage(
        err,
        "Unknown error downloading the sales order PDF."
      );
      console.error("[NetSuiteService] getSalesOrderPdf error:", message);
      throw new Error(message);
    }
  }

  public async getPriceLevels(): Promise<PriceLevel[]> {
    let paymentTerms = [] as PriceLevel[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_price_levels`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      paymentTerms = res.data;
    } catch (err) {
      console.error("Error getting the price levels", err);
    }
    return paymentTerms;
  }

  public async getPaymentTerms(): Promise<PaymentTerm[]> {
    let paymentTerms = [] as PaymentTerm[];
    try {
      const netSuiteApiUrl = `/api/netsuite/suiteql/get_payment_terms`;
      const res: AxiosResponse = await laravelApi.get(netSuiteApiUrl);
      paymentTerms = res.data;
    } catch (err) {
      console.error("Error getting the payment terms", err);
    }
    return paymentTerms;
  }
}
